/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { parseISO } from 'date-fns';
import axios, { AxiosResponse } from 'axios';
import { IoClose } from 'react-icons/io5';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { cpf } from 'cpf-cnpj-validator';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  TableList,
  Container,
  Filter,
  Modal,
  TableListModal,
  Status,
  ModalTransfer,
  ModalTransferModCat,
} from './styles';

import Table, { IColumn } from '~/components/Table';
import FiltersLists from '~/components/FiltersLists';
import ExportCsv from '~/components/ExportCsv';
import api from '~/services/api';
import { KrClose, KrEye, KrTicket } from '~/components/KoroIcons';
import Loading from '~/components/Loading';
import Select, { IOption } from '~/components/Select';
import formatDate from '~/utils/formatDate';
import InputMask from '~/components/InputMask';
import Input from '~/components/Input';
import InputRadio from '~/components/InputRadio';
import Question, { IQuestionComponent } from '~/components/Question';
import getValidationErros from '~/utils/getValidationsErrors';
import { formatPrice } from '~/utils/format';
import InputCheckbox from '~/components/InputCheckbox';
import Toast from '~/utils/toast';

interface IAnswerDetail {
  id: number;
  text: string;
  question_type_id: number;
  question_id: number;
  alternative_id?: number;
  question: {
    id: number;
    name: string;
    order: number;
    required: boolean;
    question_type_id: number;
  };
}

interface ITicketDetail {
  id: number;
  batch_category_id: number;
  modality_id: number;
  user_id: number;
  document: string;
  name: string;
  email: string;
  phone: string;
  birthdate: string | null;
  gender: string;
  kit: string | null;
  status: string;
  check_in_id: number | null;
  read: string | null;
  code: string;
  batchCategory: {
    has_age_group?: boolean;
    min_age?: number;
    max_age?: number;
    has_kit?: boolean;
    category: {
      is_child: boolean;
      is_elderly: boolean;
      is_female: boolean;
      is_male: boolean;
      has_age_group?: boolean;
      min_age?: number;
      max_age?: number;
      has_kit?: boolean;
    };
  } | null;
  answers: IAnswerDetail[];
  kitUser: {
    kitQuantity_id: number;
    id: number;
  };
}

interface IAnswer {
  id: number;
  text: string;
  question: {
    name: string;
    order: number;
  };
  event_id?: number;
  question_type_id?: number;
  question_id?: number;
  alternative_id?: number;
}

interface ITicketData {
  voucher: string;
  id: number;
  code: string;
  status: string;
  order_code: string;
  batch_name: string;
  name: string;
  document: string;
  phone: string;
  email: string;
  birthdate: string;
  gender: string;
  organizer_id: number;
  event_id: number;
  event_name: string;
  event_date: string;
  modality_name: string;
  category_name: string;
  kit?: string;
  order_date: string;
  user_id: number;
  user_name: string;
  user_phone: string;
  user_email: string;
  user_document: string;
  user_gender: string;
  city: string;
  state: string;
  order_status: string;
  checkin_id?: number;
  read?: string;
  has_questions: boolean;
  created_at: string;
  check_in_id: string;
}

export interface IAlternative {
  id: number;
  text: string;
}

interface IQuestion {
  id: number;
  question: string;
  answer: string;
  type?: 'unique-choice' | 'multiple-choice' | 'free-text';
  alternatives?: IAlternative[];
  isRequired?: boolean;
}

interface ITicket {
  voucher: any;
  batch_category_id?: number;
  modality_id?: number;
  phone: string | undefined;
  gender: any;
  batchCategory?: {
    has_age_group?: boolean;
    min_age?: number;
    max_age?: number;
    has_kit?: boolean;
    category: {
      is_child: boolean;
      is_elderly: boolean;
      is_female: boolean;
      is_male: boolean;
    };
  };
  birthdate: any;
  read: any;
  check_in_id: any;
  batch_name: string;
  id: string;
  ticket_id: number;
  status: string;
  name: string;
  document: string;
  email: string;
  event_id: number;
  event_name: string;
  event_date: string;
  modality?: string;
  category: string;
  buy_date: string;
  buyer_name: string;
  buyer_phone: string;
  buyer_email: string;
  kit: string;
  organizer: string;
  order_status: string;
  code: string;
  is_elderly?: boolean;
  is_child?: boolean;
  is_male?: boolean;
  is_female?: boolean;
  has_age_group?: boolean;
  min_age?: number;
  max_age?: number;
  has_kit?: boolean;
  orderCode?: number;
  user_id?: number;
  has_questions: boolean;
  questions: IQuestion[];
}

interface IDataItem {
  id: string;
  name: string;
  document: string;
  email: string;
  event_id: number;
  event_name: string;
  event_date: string;
  modality?: string;
  category: string;
  kit: string;
  [key: string]: any;
}

interface ITicketResponse {
  data: ITicketData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IOrganizer {
  id: number;
  name: string;
}

interface IAlternativesResponse {
  id: number;
  name: string;
}

export interface IAllQuestionResponse {
  id: number;
  event_id: number;
  question_type_id: number;
  name: string;
  order: number;
  required: boolean;
  alternatives: IAlternativesResponse[];
}

interface IQuantities {
  id: number;
  size: string;
}

interface IEventResponse {
  id: number;
  fill_before: number;
  kit2: {
    id: number;
    has_size_variation: boolean;
    quantities: IQuantities[];
  };
}

interface IParams {
  slug: string;
}

interface ISize {
  id: number;
  value: string;
  selected: boolean;
  notSelectable?: boolean;
}

interface ICheckTicket {
  id: number;
  batch_category_id: number;
  modality_id: number;
  event: {
    unique_ticket: boolean;
  };
  batchCategory: {
    category_id: number;
    modality_id: number;
  };
}

interface IFormData {
  document: string;
  name: string;
  phone: string;
  email: string;
  documents: string;
  color: string;
  size: string;
  birthdate: string;
  gender: string;
}

interface IOrderData {
  status: string;
  paymentMethod: string;
  price: string;
  qrCode?: string;
  tickets: ITicket[];
  created_at: string;
}

interface IOrder {
  [key: string]: IOrderData;
}

interface IAvailable {
  selected: any;
  price: number;
  batch_category_id: number;
  category: string;
  modality: string;
}

interface IModCat {
  current: {
    batch: string;
    name: string;
    price: number;
    category: string;
    purchase_date: string;
    modality: string;
  };
  availables: IAvailable[];
}

const Users: React.FC = () => {
  const [pageSelected, setPageSelected] = useState(1);
  const [tableData, setTableData] = useState({
    from: 0,
    to: 0,
    total: 0,
    totalPage: 0,
    generalTotal: 0,
  });
  const formRef = React.useRef<FormHandles>(null);
  const [sizes, setSizes] = useState<ISize[]>([]);
  const [tickets, setTickets] = useState<ITicket[]>([]);
  const [statusSelected, setStatusSelected] = useState<string | undefined>();
  const [event, setEvent] = useState<string | undefined>();
  const [questions, setQuestions] = useState<IQuestion[]>([]);
  const [show, setShow] = useState(false);
  const [quantity, setQuantity] = useState(10);
  const [dataCsv, setDataCsv] = useState<IDataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [organizerId, setOrganizerId] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<string | undefined>(undefined);
  const [statusPayment, setStatusPayment] = useState<string | undefined>();
  const [documentTicket, setDocumentTicket] = useState<string | undefined>();
  const [codeVoucher, setCodeVoucher] = useState<string | undefined>();
  const [ticketFilterId, setTicketFilterId] = useState<string | undefined>();
  const [organizers, setOrganizers] = useState<IOption[]>([]);
  const [showTransfer, setShowTransfer] = useState(false);
  const [ticketSelected, setTicketSelected] = useState<ITicket>({} as ITicket);
  const [allQuestions, setAllQuestions] = useState<IQuestionComponent[]>([]);
  const [eventId, setEventId] = useState(0);
  const [genderSelected, setGenderSelected] = useState({} as IOption);
  const [hasSizeVariation, setHasSizeVariation] = useState(false);
  const [kitId, setKitId] = useState<number | undefined>(undefined);
  const [answers, setAnswers] = useState<IAnswer[]>([]);
  const [orders, setOrders] = useState({} as IOrder);
  const [ticketError, setTicketError] = useState('');
  const [nameTicket, setNameTicket] = useState<string | undefined>(undefined);
  const [kitUserId, setKitUserId] = useState<number | undefined>(undefined);
  const [kitQuantityId, setKitQuantityId] = useState<number | undefined>(
    undefined
  );
  const [answersTransfer, setAnswersTransfer] = useState<{
    [key: number]: any;
  }>({});
  const [showModCat, setShowModCat] = useState(false);
  const [modCat, setModCat] = useState<IModCat | null>(null);
  const [selectedModCat, setSelectedModCat] = useState<number | null>(null);

  const genderOptions = useMemo<IOption[]>(
    () => [
      { id: 'M', value: 'Masculino', notSelectable: false, selected: false },
      { id: 'F', value: 'Feminino', notSelectable: false, selected: false },
    ],
    []
  );

  const allSizes = useMemo(
    () => [
      {
        id: 'PP',
        value: 'PP',
        selected: false,
      },
      {
        id: 'P',
        value: 'P',
        selected: false,
      },
      {
        id: 'M',
        value: 'M',
        selected: false,
      },
      {
        id: 'G',
        value: 'G',
        selected: false,
      },
      {
        id: 'GG',
        value: 'GG',
        selected: false,
      },
      {
        id: 'XG',
        value: 'XG',
        selected: false,
      },
      {
        id: 'XXG',
        value: 'XXG',
        selected: false,
      },
      {
        id: 'PP_BABY',
        value: 'PP BABYLOOK',
        selected: false,
      },
      {
        id: 'P_BABY',
        value: 'P BABYLOOK',
        selected: false,
      },
      {
        id: 'M_BABY',
        value: 'M BABYLOOK',
        selected: false,
      },
      {
        id: 'G_BABY',
        value: 'G BABYLOOK',
        selected: false,
      },
      {
        id: 'GG_BABY',
        value: 'GG BABYLOOK',
        selected: false,
      },
      {
        id: 'XG_BABY',
        value: 'XG BABYLOOK',
        selected: false,
      },
      {
        id: 'XXG_BABY',
        value: 'XXG BABYLOOK',
        selected: false,
      },
      {
        id: '2',
        value: '2 ANOS',
        selected: false,
      },
      {
        id: '4',
        value: '4 ANOS',
        selected: false,
      },
      {
        id: '6',
        value: '6 ANOS',
        selected: false,
      },
      {
        id: '8',
        value: '8 ANOS',
        selected: false,
      },
      {
        id: '10',
        value: '10 ANOS',
        selected: false,
      },
      {
        id: '12',
        value: '12 ANOS',
        selected: false,
      },
      {
        id: '14',
        value: '14 ANOS',
        selected: false,
      },
    ],
    []
  );

  const formatKitName = useCallback((kit: string | undefined): string => {
    if (!kit) return 'Não Possui';
    // eslint-disable-next-line no-nested-ternary
    return kit.toLowerCase().includes('baby')
      ? `Babylook: ${kit.replace('_BABY', '')}`
      : !Number.isNaN(parseInt(kit, 10))
      ? `${kit} ANOS`
      : kit;
  }, []);

  const sizesWithSelection = useMemo(() => {
    const matchedSize = [...allSizes]
      .sort((a, b) => b.value.length - a.value.length)
      .find(
        (s) =>
          !!ticketSelected.kit &&
          ticketSelected.kit !== 'Não Possui' &&
          ticketSelected.kit === s.id
      );

    const mappedSizes = sizes.map((size) => ({
      ...size,
      selected: !!matchedSize && size.value === matchedSize.value,
    }));

    // Se o tamanho do ticket não está nos tamanhos disponíveis do evento,
    // insere no início já marcado como selecionado
    if (matchedSize && !sizes.some((s) => s.value === matchedSize.value)) {
      return [{ ...matchedSize, selected: true }, ...mappedSizes];
    }

    return mappedSizes;
  }, [allSizes, sizes, ticketSelected.kit]);

  useEffect(() => {
    if (eventId) {
      api
        .get<IAllQuestionResponse[]>(`questions/events/${eventId}`)
        .then((response) => {
          const data = response.data.map<IQuestionComponent>((question) => {
            let type = 'free-text';

            if (question.question_type_id === 2) {
              type = 'unique-choice';
            }

            if (question.question_type_id === 3) {
              type = 'multiple-choice';
            }

            const alternatives = question.alternatives.map((alternative) => ({
              id: alternative.id,
              text: alternative.name,
              new: false,
            }));

            return {
              id: question.id,
              question: question.name,
              type: type as IQuestionComponent['type'],
              order: question.order,
              alternatives,
              isRequired: question.required,
            };
          });
          setAllQuestions(data);
        });
    }
  }, [eventId]);

  useEffect(() => {
    api.get<IOrganizer[]>('organizers').then(async (response) => {
      const dataOrganizers = await response.data.map<IOption>((organizer) => ({
        id: organizer.id,
        value: organizer.name,
        selected: false,
      }));
      setOrganizers(dataOrganizers);
    });
  }, []);

  const handleChangeDocument = useCallback(
    async (e) => {
      const { value } = e.target;

      if (value.length === 14) {
        const response = await api.get(`tickets/document/${value}`);
        if (response.data.length > 0) {
          const selectedGender =
            genderOptions.find((opt) => opt.id === response.data[0].gender) ||
            ({} as IOption);
          setGenderSelected(selectedGender);
          setTicketSelected((oldState) => ({
            ...oldState,
            document: value,
            name: response.data[0].name,
            phone: response.data[0].phone,
            email: response.data[0].email,
            birthdate: response.data[0].birthdate
              ? formatDate(response.data[0].birthdate, 'dd/MM/yyyy')
              : '',
            gender: response.data[0].gender || '',
          }));
          formRef.current?.setFieldValue('name', response.data[0].name);
          formRef.current?.setFieldValue('email', response.data[0].email);
          formRef.current?.setFieldValue(
            'gender',
            response.data[0].gender || ''
          );
        } else {
          const responseAdditionalInformation = await api.get(
            `additional-informations/document/${value}`
          );
          if (responseAdditionalInformation.data) {
            const selectedGender =
              genderOptions.find(
                (opt) => opt.id === responseAdditionalInformation.data.gender
              ) || ({} as IOption);
            setGenderSelected(selectedGender);
            setTicketSelected((oldState) => ({
              ...oldState,
              document: value,
              name: responseAdditionalInformation.data.user.name,
              phone: responseAdditionalInformation.data.phone,
              email: responseAdditionalInformation.data.user.email,
              birthdate: responseAdditionalInformation.data.birthdate
                ? formatDate(
                    responseAdditionalInformation.data.birthdate,
                    'dd/MM/yyyy'
                  )
                : '',
              gender: responseAdditionalInformation.data.gender || '',
            }));
            formRef.current?.setFieldValue(
              'name',
              responseAdditionalInformation.data.user.name
            );
            formRef.current?.setFieldValue(
              'email',
              responseAdditionalInformation.data.user.email
            );
            formRef.current?.setFieldValue(
              'gender',
              responseAdditionalInformation.data.gender || ''
            );
          }
        }
      }
    },
    [genderOptions]
  );

  const handleClick = useCallback(async (ticketId) => {
    const response = await api.get<IAnswer[]>(`answers/tickets/${ticketId}`);
    const questionsData = response.data.map<IQuestion>((answer) => {
      // descomentar abaixo
      // const response = await api.get<IQuestionResponse[]>(
      //   `questions/tickets/${ticketId}`
      // );
      // const questionsData = response.data.map<IQuestion>((question) => {
      return {
        // excluir depois daqui
        id: answer.question.order,
        question: answer.question.name,
        answer: answer.text,
        // até aqui

        // descomentar abaixo
        // id: question.order,
        // question: question.name,
        // answer: question.answers[0]?.text || '-',
      };
    });
    setQuestions(questionsData.sort((a, b) => a.id - b.id));
    setShow(true);
  }, []);

  const columnsModal = useMemo<IColumn[]>(
    () => [
      {
        name: 'Id',
        selector: 'id',
        sortable: false,
      },

      {
        name: 'Perguntas',
        selector: 'question',
        sortable: false,
      },
      {
        name: 'Respostas',
        selector: 'answer',
        sortable: false,
      },
    ],
    []
  );

  const status = useMemo(
    () => [
      {
        id: 'Disponível',
        value: 'Não Preenchido',
        selected: false,
      },
      {
        id: 'Usado',
        value: 'Preenchido',
        selected: false,
      },
      {
        id: 'Processando',
        value: 'Processando',
        selected: false,
      },
      {
        id: 'Devolvido',
        value: 'Devolvido',
        selected: false,
      },
      {
        id: 'Cancelado',
        value: 'Cancelado',
        selected: false,
      },
      {
        id: 'Cortesia',
        value: 'Cortesia',
        selected: false,
      },
    ],
    []
  );

  const paymentStatus = useMemo(
    () => [
      {
        id: 'Pago',
        value: 'Pago',
        selected: false,
      },
      {
        id: 'Aguardando pagamento',
        value: 'Aguardando pagamento',
        selected: false,
      },
      {
        id: 'Cancelado',
        value: 'Cancelado',
        selected: false,
      },
      {
        id: 'Expirado',
        value: 'Expirado',
        selected: false,
      },
      {
        id: 'Erro no pagamento',
        value: 'Erro no pagamento',
        selected: false,
      },
    ],
    []
  );

  const handleLoadTickets = useCallback(
    async (
      page,
      organizer_id?: string,
      eventData?: string,
      order_id?: string,
      paymentStatusData?: string,
      statusData?: string,
      quantityData?: number,
      documentData?: string,
      nameData?: string,
      codeVoucherData?: string,
      ticketData?: string
    ) => {
      setLoading(true);
      const response: AxiosResponse<ITicketResponse> = await api
        .get(`tickets`, {
          params: {
            page,
            organizer_id,
            event: eventData,
            order_id: order_id?.toString().slice(12) || order_id,
            payment_status: paymentStatusData ? [paymentStatusData] : [],
            status: statusData ? [statusData] : [],
            allEventsStatus: true,
            quantity: quantityData,
            document: documentData,
            name: nameData,
            voucher: codeVoucherData,
            ticket_id: ticketData,
          },
        })
        .finally(() => setLoading(false));

      const dataTicket = response.data.data.map((ticket) => {
        return {
          id: `${ticket.code}`,
          status: ticket.status,
          ticket_id: ticket.id,
          organizer:
            organizers.find((org) => org.id === ticket.organizer_id)?.value ||
            '-',
          code: ticket.order_code || '-',
          voucher: ticket.voucher || '-',
          name: ticket.name || '-',
          document: ticket.document || '-',
          email: ticket.email || '-',
          phone: ticket.phone || '-',
          birthdate: ticket.birthdate
            ? formatDate(new Date(ticket.birthdate), 'dd/MM/yyyy')
            : '-',
          gender: ticket.gender || '-',
          batch_name: ticket.batch_name || '-',
          event_id: ticket.event_id,
          event_name: ticket.event_name || '-',
          event_date: formatDate(parseISO(ticket.event_date), 'dd/MM/yyyy'),
          modality: ticket.modality_name || '-',
          category: ticket.category_name || '-',
          order_status: ticket.order_status || '-',
          kit: formatKitName(ticket.kit),
          buy_date: formatDate(parseISO(ticket.order_date), 'dd/MM/yyyy'),
          buyer_name: ticket.user_name || '-',
          buyer_phone: ticket.user_phone || '-',
          buyer_email: ticket.user_email || '-',
          check_in_id: ticket.checkin_id ?? '-',
          read: ticket.read
            ? formatDate(new Date(ticket.read), 'dd/MM/yyyy HH:mm:ss')
            : '-',
          has_questions: ticket.has_questions,
          questions: [],
        };
      });

      setTickets(dataTicket);
      setTableData((oldState) => ({
        from: response.data.from,
        to: response.data.to,
        total: response.data.total,
        totalPage: response.data.pages,
        generalTotal: oldState.generalTotal
          ? oldState.generalTotal
          : response.data.total,
      }));
    },
    [formatKitName, organizers]
  );

  const handleClose = useCallback(() => {
    setShow(false);
  }, []);

  const handleClickTransfer = useCallback(
    async (row: ITicket) => {
      const responseEvent = await api.get<IEventResponse>(
        `events/${row.event_id}`,
        {
          params: {
            enviroment: 'atleta',
          },
        }
      );
      // const responseAnswers = await api.get<IAnswer[]>(
      //   `answers/tickets/${row.ticket_id}`
      // );

      if (responseEvent.data.kit2) {
        if (responseEvent.data.kit2.has_size_variation) {
          const sizesData = responseEvent.data.kit2.quantities.map(
            (quantitySize) => ({
              id: quantitySize.id,
              // eslint-disable-next-line no-nested-ternary
              value: quantitySize.size.includes('_BABY')
                ? quantitySize.size.replaceAll('_BABY', ' BABYLOOK')
                : !Number.isNaN(parseInt(quantitySize.size, 10))
                ? `${quantitySize.size} ANOS`
                : quantitySize.size,
              selected: false,
              notSelectable: false,
            })
          );
          setSizes(sizesData);
        }
        setHasSizeVariation(responseEvent.data.kit2.has_size_variation);
        setKitId(responseEvent.data.kit2.id);
      }

      const response = await api.get<ITicketDetail>(`tickets/${row.ticket_id}`);
      const ticketData = response.data;

      const getQuestionType = (typeId: number): IQuestion['type'] => {
        if (typeId === 2) return 'unique-choice';
        if (typeId === 3) return 'multiple-choice';
        return 'free-text';
      };

      const questionsData = (ticketData.answers || []).map<IQuestion>(
        (answer) => ({
          id: answer.question.order,
          question: answer.question.name,
          answer: answer.text,
          type: getQuestionType(answer.question_type_id),
          isRequired: answer.question.required,
        })
      );

      const mappedTicket: ITicket = {
        ...row,
        batch_category_id: ticketData.batch_category_id,
        modality_id: ticketData.modality_id,
        user_id: ticketData.user_id,
        document: ticketData.document,
        name: ticketData.name,
        email: ticketData.email,
        phone: ticketData.phone,
        birthdate: ticketData.birthdate
          ? formatDate(new Date(ticketData.birthdate), 'dd/MM/yyyy')
          : '',
        gender: ticketData.gender,
        kit: ticketData.kit || 'Não Possui',
        status: ticketData.status,
        check_in_id: ticketData.check_in_id,
        read: ticketData.read,
        batchCategory: ticketData.batchCategory ?? undefined,
        is_elderly: ticketData.batchCategory?.category?.is_elderly,
        is_child: ticketData.batchCategory?.category?.is_child,
        is_male: ticketData.batchCategory?.category?.is_male,
        is_female: ticketData.batchCategory?.category?.is_female,
        has_age_group: ticketData.batchCategory?.category?.has_age_group,
        min_age: ticketData.batchCategory?.category?.min_age,
        max_age: ticketData.batchCategory?.category?.max_age,
        has_kit: ticketData.batchCategory?.category?.has_kit,
        questions: questionsData,
      };

      const selectedGender =
        genderOptions.find((opt) => opt.id === ticketData.gender) ||
        ({} as IOption);
      setGenderSelected(selectedGender);
      setTicketSelected(mappedTicket);
      setEventId(row.event_id);
      setShowTransfer(true);
      setKitUserId(ticketData.kitUser?.id);
      setKitQuantityId(ticketData.kitUser?.kitQuantity_id);
      setAnswers(ticketData.answers);
    },
    [genderOptions]
  );

  const handleClickTransferModCat = useCallback(async (row: ITicket) => {
    try {
      const response = await api.get(
        `batches-categories/changes/verify/${row.ticket_id}`
      );
      setTicketSelected(row);
      setModCat(response.data);
      setSelectedModCat(null);

      setShowModCat(true);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Ocorreu um erro inesperado';

      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: errorMessage,
      });
    }
  }, []);

  useEffect(() => {
    handleLoadTickets(1);
  }, [handleLoadTickets]);

  const columns = useMemo<IColumn[]>(
    () => [
      {
        name: 'Inscrição',
        selector: 'id',
        sortable: true,
      },
      {
        name: 'Status',
        selector: 'status',
        cell: (row) => (
          <span>
            {(row.status === 'Usado' && 'Preenchido') ||
              (row.status === 'Disponível' && 'Não Preenchido') ||
              row.status}
          </span>
        ),
      },
      {
        name: 'Organizador',
        selector: 'organizer',
        sortable: true,
      },
      {
        name: 'Pedido',
        selector: 'code',
        sortable: true,
      },
      {
        name: 'Cupom',
        selector: 'voucher',
        sortable: true,
      },
      {
        name: 'Lote',
        selector: 'batch_name',
        sortable: true,
      },
      {
        name: 'Nome',
        selector: 'name',
        sortable: true,
      },
      {
        name: 'Documento',
        selector: 'document',
        sortable: true,
      },
      {
        name: 'E-mail',
        selector: 'email',
        sortable: true,
      },
      {
        name: 'Nascimento',
        selector: 'birthdate',
        sortable: true,
      },
      {
        name: 'Gênero',
        selector: 'gender',
        sortable: true,
      },
      {
        name: 'Id do evento',
        selector: 'event_id',
        sortable: false,
      },
      {
        name: 'Nome do evento',
        selector: 'event_name',
        sortable: true,
      },
      {
        name: 'Data do evento',
        selector: 'event_date',
        sortable: false,
      },
      {
        name: 'Modalidade',
        selector: 'modality',
        sortable: true,
      },
      {
        name: 'Categoria',
        selector: 'category',
        sortable: true,
      },
      {
        name: 'Kit',
        selector: 'kit',
        sortable: true,
      },
      {
        name: 'Data compra',
        selector: 'buy_date',
        sortable: false,
      },
      {
        name: 'Nome comprador',
        selector: 'buyer_name',
        sortable: true,
      },
      {
        name: 'Fone comprador',
        selector: 'buyer_phone',
        sortable: true,
      },
      {
        name: 'Email comprador',
        selector: 'buyer_email',
        sortable: true,
      },
      {
        name: 'Status Pag',
        selector: 'order_status',
        cell: (row: ITicket) => (
          <Status
            status={row.order_status}
            className="text-white rounded-pill mb-0"
          >
            {row.order_status}
          </Status>
        ),
      },
      {
        name: 'Check-in Id',
        selector: 'check_in_id',
        sortable: true,
      },
      {
        name: 'Data/Hora Check-in',
        selector: 'read',
        sortable: true,
      },
      {
        name: 'Perguntas',
        selector: 'questions',
        cell: (row: ITicket) => (
          <button
            type="button"
            onClick={() => handleClick(row.ticket_id)}
            disabled={!row.has_questions}
            className={`${
              row.has_questions ? 'visible' : 'opacity-50'
            } btn-icons border-0`}
          >
            <KrEye size={16} color="#fff" />
          </button>
        ),
      },
      {
        name: 'Transf. Inscrição',
        selector: 'transfer',
        cell: (row: ITicket) => (
          <button
            type="button"
            onClick={() => {
              handleClickTransfer(row);
            }}
            disabled={
              row.order_status !== 'Pago' && row.order_status !== 'Cortesia'
            }
            className={`${
              row.order_status === 'Pago' || row.order_status === 'Cortesia'
                ? 'visible'
                : 'opacity-50'
            } btn-icons border-0`}
          >
            <KrTicket size={16} color="#fff" />
          </button>
        ),
      },
      {
        name: 'Transf. Mod/Cat',
        selector: 'transfer_mod_cat',
        cell: (row: ITicket) => (
          <button
            type="button"
            onClick={() => {
              handleClickTransferModCat(row);
            }}
            disabled={
              row.order_status !== 'Pago' && row.order_status !== 'Cortesia'
            }
            className={`${
              row.order_status === 'Pago' || row.order_status === 'Cortesia'
                ? 'visible'
                : 'opacity-50'
            } btn-icons border-0`}
          >
            <KrTicket size={16} color="#fff" />
          </button>
        ),
      },
    ],
    [handleClick, handleClickTransfer, handleClickTransferModCat]
  );

  const handleChangePage = useCallback(
    (page) => {
      handleLoadTickets(
        page,
        organizerId,
        event,
        order,
        statusPayment,
        statusSelected,
        quantity,
        documentTicket,
        nameTicket,
        codeVoucher,
        ticketFilterId
      );
      setPageSelected(page);
    },
    [
      handleLoadTickets,
      organizerId,
      event,
      order,
      statusPayment,
      statusSelected,
      quantity,
      documentTicket,
      nameTicket,
      codeVoucher,
      ticketFilterId,
    ]
  );

  const handleSubmitFilters = useCallback(
    (formData) => {
      setOrganizerId(
        formData.organizer !== 'todos' ? formData.organizer : undefined
      );
      setEvent(
        formData.event === 'Todos' ? undefined : formData.event || undefined
      );
      setOrder(formData.order || undefined);

      setStatusPayment(
        formData.statusPayment !== 'todos' ? formData.statusPayment : undefined
      );
      setStatusSelected(
        formData.statusTicket !== 'todos' ? formData.statusTicket : undefined
      );
      setDocumentTicket(formData.document || undefined);
      setNameTicket(formData.name || undefined);
      setCodeVoucher(formData.codeVoucher || undefined);
      setTicketFilterId(formData.ticket || undefined);
      handleLoadTickets(
        1,
        formData.organizer !== 'todos' ? formData.organizer : undefined,
        formData.event === 'Todos' ? undefined : formData.event || undefined,
        formData.order || undefined,
        formData.statusPayment !== 'todos' ? formData.statusPayment : undefined,
        formData.statusTicket !== 'todos' ? formData.statusTicket : undefined,
        quantity,
        formData.document || undefined,
        formData.name || undefined,
        formData.codeVoucher || undefined,
        formData.ticket || undefined
      );
      setPageSelected(1);
    },
    [handleLoadTickets, quantity]
  );

  const handleChangeDataCsv = useCallback((data: ITicket[]) => {
    const dataAux = data.map<IDataItem>((ticket) => {
      const questionsData = ticket.questions.reduce((acc, question, index) => {
        acc[`questao ${index.toString().padStart(2, '0')}`] = `Q ${question.id
          .toString()
          .padStart(2, '0')}`;
        acc[`resposta ${index.toString().padStart(2, '0')}`] = question.answer;
        return acc;
      }, {} as any);

      return {
        id: ticket.id,
        status:
          (ticket.status === 'Usado' && 'Preenchido') ||
          (ticket.status === 'Disponível' && 'Não Preenchido') ||
          ticket.status,
        ticket_id: ticket.ticket_id,
        organizer: ticket.organizer,
        voucher: ticket.voucher,
        request_number: `#${ticket.code}`,
        name: ticket.name,
        document: ticket.document,
        email: ticket.email,
        birthdate: ticket.birthdate,
        gender: ticket.gender,
        event_id: ticket.event_id,
        event_name: ticket.event_name,
        event_date: ticket.event_date,
        modality: ticket.modality,
        category: ticket.category,
        kit: ticket.kit,
        batch_name: ticket.batch_name,
        buy_date: ticket.buy_date,
        buyer_name: ticket.buyer_name,
        buyer_phone: ticket.buyer_phone,
        buyer_email: ticket.buyer_email,
        order_status: ticket.order_status,
        check_in_id: ticket.check_in_id,
        read: ticket.read,
        ...questionsData,
      };
    });

    setDataCsv(dataAux);

    return dataAux;
  }, []);

  const columnsCsv = useMemo<IColumn[]>(() => {
    const questionKeys = Array.from(
      new Set(
        dataCsv.flatMap((item) =>
          Object.keys(item).filter((key) =>
            /^questao \d+$|^resposta \d+$/.test(key)
          )
        )
      )
    );

    return [
      {
        name: '#',
        selector: 'id',
      },
      {
        name: 'Status',
        selector: 'status',
      },
      {
        name: 'Organizador',
        selector: 'organizer',
      },
      {
        name: 'Num. Pedido',
        selector: 'request_number',
      },
      {
        name: 'Cupom',
        selector: 'voucher',
      },
      {
        name: 'Lote',
        selector: 'batch_name',
      },
      {
        name: 'Nome',
        selector: 'name',
      },
      {
        name: 'Documento',
        selector: 'document',
      },
      {
        name: 'E-mail',
        selector: 'email',
      },
      {
        name: 'Nascimento',
        selector: 'birthdate',
      },
      {
        name: 'Gênero',
        selector: 'gender',
      },
      {
        name: 'Id do evento',
        selector: 'event_id',
      },
      {
        name: 'Nome do evento',
        selector: 'event_name',
      },
      {
        name: 'Data do evento',
        selector: 'event_date',
      },
      {
        name: 'Modalidade',
        selector: 'modality',
      },
      {
        name: 'Categoria',
        selector: 'category',
      },
      {
        name: 'Kit',
        selector: 'kit',
      },
      {
        name: 'Data compra',
        selector: 'buy_date',
      },
      {
        name: 'Nome comprador',
        selector: 'buyer_name',
      },
      {
        name: 'Fone comprador',
        selector: 'buyer_phone',
      },
      {
        name: 'Email comprador',
        selector: 'buyer_email',
      },
      {
        name: 'Status Pag',
        selector: 'order_status',
      },
      {
        name: 'Check-in Id',
        selector: 'check_in_id',
      },
      {
        name: 'Data/Hora Check-in',
        selector: 'read',
      },
      ...questionKeys.map((questionKey) => ({
        name: '',
        selector: questionKey,
      })),
    ];
  }, [dataCsv]);

  const handleClickExportCsv = useCallback(async () => {
    setLoading(true);
    const response = await api
      .get<ITicketData[]>(`tickets`, {
        params: {
          organizer_id: organizerId,
          event,
          order_id: order?.toString().slice(12) || order,
          payment_status: statusPayment ? [statusPayment] : [],
          status: statusSelected ? [statusSelected] : [],
          allEventsStatus: true,
          document: documentTicket,
          name: nameTicket,
          voucher: codeVoucher,
          ticket_id: ticketFilterId,
        },
      })
      .finally(() => setLoading(false));

    const dataTicket = response.data.map((ticket) => {
      return {
        id: `#${ticket.code}`,
        ticket_id: ticket.id,
        status: ticket.status,
        organizer:
          organizers.find((org) => org.id === ticket.organizer_id)?.value ||
          '-',
        code: ticket.order_code || '-',
        voucher: ticket.voucher || '-',
        name: ticket.name || '-',
        document: ticket.document || '-',
        email: ticket.email || '-',
        birthdate: ticket.birthdate
          ? formatDate(new Date(ticket.birthdate), 'dd/MM/yyyy')
          : '-',
        gender: ticket.gender || '-',
        phone: ticket.phone || '-',
        batch_name: ticket.batch_name || '-',
        event_id: ticket.event_id,
        event_name: ticket.event_name || '-',
        event_date: formatDate(parseISO(ticket.event_date), 'dd/MM/yyyy'),
        modality: ticket.modality_name || '-',
        category: ticket.category_name || '-',
        order_status: ticket.order_status || '-',
        kit: formatKitName(ticket.kit),
        buy_date: formatDate(parseISO(ticket.order_date), 'dd/MM/yyyy'),
        buyer_name: ticket.user_name || '-',
        buyer_phone: ticket.user_phone || '-',
        buyer_email: ticket.user_email || '-',
        check_in_id: ticket.checkin_id ?? '-',
        read: ticket.read
          ? formatDate(new Date(ticket.read), 'dd/MM/yyyy HH:mm:ss')
          : '-',
        has_questions: ticket.has_questions,
        questions: [],
      };
    });

    const dataCsvAux = handleChangeDataCsv(dataTicket);

    return dataCsvAux;
  }, [
    organizerId,
    event,
    order,
    statusPayment,
    statusSelected,
    documentTicket,
    nameTicket,
    codeVoucher,
    ticketFilterId,
    handleChangeDataCsv,
    organizers,
    formatKitName,
  ]);

  const handleChangeQuantity = useCallback(
    (quantityData) => {
      setQuantity(quantityData);
      handleLoadTickets(
        1,
        organizerId,
        event,
        order,
        statusPayment,
        statusSelected,
        quantityData,
        documentTicket,
        nameTicket,
        codeVoucher,
        ticketFilterId
      );
      setPageSelected(1);
    },
    [
      event,
      handleLoadTickets,
      order,
      organizerId,
      statusPayment,
      statusSelected,
      documentTicket,
      nameTicket,
      codeVoucher,
      ticketFilterId,
    ]
  );

  const handleCloseTransfer = useCallback(() => {
    setShowTransfer(false);
    setTicketSelected({} as ITicket);
    setSizes([]);
  }, []);

  const handleCloseModCat = useCallback(() => {
    setShowModCat(false);
  }, []);

  const handleSubmit = useCallback(
    async (data: IFormData) => {
      const calculateAge = (
        birthdateStr: string,
        referenceDateStr?: string
      ): number | null => {
        if (!birthdateStr) return null;
        let birth: Date;
        if (
          birthdateStr.includes('T') ||
          /^\d{4}-\d{2}-\d{2}/.test(birthdateStr)
        ) {
          const d = new Date(birthdateStr);
          if (Number.isNaN(d.getTime())) return null;
          birth = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
        } else {
          const parts = birthdateStr.split('/');
          if (parts.length !== 3) return null;
          const [day, month, year] = parts;
          birth = new Date(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10)
          );
        }

        if (Number.isNaN(birth.getTime())) return null;
        let ref: Date;

        if (!referenceDateStr) return null;

        if (
          referenceDateStr.includes('T') ||
          /^\d{4}-\d{2}-\d{2}/.test(referenceDateStr)
        ) {
          const d = new Date(referenceDateStr);
          if (Number.isNaN(d.getTime())) return null;
          ref = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
        } else {
          const parts = referenceDateStr.split('/');
          if (parts.length !== 3) return null;
          const [day, month, year] = parts;
          ref = new Date(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10)
          );
        }
        let age = ref.getFullYear() - birth.getFullYear();
        const m = ref.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && ref.getDate() < birth.getDate())) age -= 1;
        return age;
      };

      try {
        setLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          document: Yup.string()
            .nullable()
            .required('O CPF é obrigatório')
            .test('cpf', 'CPF inválido', (value) =>
              cpf.isValid(value as string)
            )
            .test(
              'check-no-duplicate-documents',
              'Este documento já foi usado em outra inscrição com esta categoria e modalidade',
              async (value, context) => {
                const response = await api.get<ICheckTicket[]>(
                  `tickets/events/${eventId}/${value}`
                );

                if (response.data.length === 0) return true;

                const { unique_ticket } = response.data[0].event;

                if (
                  unique_ticket &&
                  response.data[0].id !== ticketSelected.ticket_id
                ) {
                  return context.createError({
                    message: 'Este documento já foi usado nesta inscrição',
                  });
                }

                const checkTicket = response.data.some(
                  (ticketData) =>
                    ticketData.batch_category_id ===
                      ticketSelected.batch_category_id &&
                    ticketData.modality_id === ticketSelected.modality_id &&
                    ticketData.id !== ticketSelected.ticket_id
                );

                return !checkTicket;
              }
            ),
          name: Yup.string()
            .required('O nome é obrigatório')
            .test('full-name', 'Informe nome e sobrenome', (value) => {
              if (!value) return false;

              const parts = value.trim().split(/\s+/).filter(Boolean);

              return parts.length >= 2;
            }),
          birthdate: Yup.string()
            .required('A data de nascimento é obrigatória')
            .test('check_age_restrictions', '', (value, context) => {
              const age = calculateAge(
                data.birthdate,
                ticketSelected.event_date
              );

              if (ticketSelected.has_age_group) {
                const belowMin =
                  ticketSelected.min_age !== undefined &&
                  (age === null || age < ticketSelected.min_age);
                const aboveMax =
                  ticketSelected.max_age !== undefined &&
                  (age === null || age > ticketSelected.max_age);

                if (belowMin || aboveMax) {
                  let message =
                    'Esta categoria não permite a idade informada na data do evento';

                  if (
                    ticketSelected.min_age !== undefined &&
                    ticketSelected.max_age !== undefined
                  ) {
                    message = `Esta categoria é exclusiva para pessoas de ${ticketSelected.min_age} até ${ticketSelected.max_age} anos na data do evento`;
                  } else if (ticketSelected.min_age !== undefined) {
                    message = `Esta categoria é exclusiva para pessoas a partir de ${ticketSelected.min_age} anos na data do evento`;
                  } else if (ticketSelected.max_age !== undefined) {
                    message = `Esta categoria é exclusiva para pessoas com até ${ticketSelected.max_age} anos na data do evento`;
                  }

                  return context.createError({ message });
                }

                return true;
              }

              if (ticketSelected.is_child && ticketSelected.is_elderly) {
                const childMaxAge = ticketSelected.max_age ?? 12;
                const isElderlyValid = age !== null && age >= 60;
                const isChildValid =
                  age !== null &&
                  (ticketSelected.min_age === undefined ||
                    age >= ticketSelected.min_age) &&
                  age <= childMaxAge;

                if (!isElderlyValid && !isChildValid) {
                  const isTeen = childMaxAge > 12 && childMaxAge < 18;
                  const childNoun = isTeen ? 'adolescente' : 'criança';
                  const childText =
                    ticketSelected.min_age !== undefined
                      ? `${childNoun}s de ${ticketSelected.min_age} até ${childMaxAge} anos`
                      : `${childNoun} até ${childMaxAge} anos`;

                  return context.createError({
                    message: `Esta categoria é exclusiva para idoso 60+ ou ${childText} na data do evento`,
                  });
                }

                return true;
              }

              if (ticketSelected.is_child) {
                const maxAge = ticketSelected.max_age ?? 12;
                const isValid =
                  age !== null &&
                  (ticketSelected.min_age === undefined ||
                    age >= ticketSelected.min_age) &&
                  age <= maxAge;

                if (!isValid) {
                  const isTeen = maxAge > 12 && maxAge < 18;
                  const childNoun = isTeen ? 'adolescente' : 'criança';
                  const message = ticketSelected.min_age
                    ? `Esta categoria é exclusiva para ${childNoun}s de ${ticketSelected.min_age} até ${maxAge} anos na data do evento`
                    : `Esta categoria é exclusiva para ${childNoun}s com até ${maxAge} anos na data do evento`;

                  return context.createError({ message });
                }

                return true;
              }

              if (ticketSelected.is_elderly) {
                if (age === null || age < 60) {
                  return context.createError({
                    message:
                      'Esta categoria é exclusiva para pessoas com 60 anos ou mais na data do evento',
                  });
                }

                return true;
              }

              return true;
            }),
          gender: Yup.string()
            .required('O gênero é obrigatório')
            .test(
              'check_male',
              'Esta categoria é exclusiva para o sexo masculino',
              () => {
                if (!ticketSelected.is_male) return true;
                return data.gender === 'M';
              }
            )
            .test(
              'check_female',
              'Esta categoria é exclusiva para o sexo feminino',
              () => {
                if (!ticketSelected.is_female) return true;
                return data.gender === 'F';
              }
            ),
          phone: Yup.string().nullable().required('O telefone é obrigatório'),
          email: Yup.string().email().required('O e-mail é obrigatório'),
          color: Yup.string().when(`$checkColor`, {
            is: (checkColor: boolean) => checkColor,
            then: Yup.string().required('A cor é obrigatória'),
            otherwise: Yup.string(),
          }),
          size: Yup.string().test(
            'check_size',
            'O tamanho é obrigatório',
            () => {
              return ticketSelected.has_kit && hasSizeVariation
                ? !!data.size
                : true;
            }
          ),
          ...allQuestions.reduce((previous, question) => {
            if (question.isRequired) {
              if (question.type === 'free-text') {
                return {
                  ...previous,
                  [`answer-text-${question.id}`]: Yup.string().test(
                    'check_text',
                    'A resposta é obrigatória',
                    (value: any) => {
                      return !!value;
                    }
                  ),
                };
              }
              if (question.type === 'unique-choice') {
                return {
                  ...previous,
                  [`answer-radio-${question.id}`]: Yup.string().test(
                    'check_radio',
                    'A resposta é obrigatória',
                    (value: any) => {
                      return !!value;
                    }
                  ),
                };
              }
              if (question.type === 'multiple-choice') {
                return {
                  ...previous,
                  [`answer-multiple-${question.id}`]: Yup.string().test(
                    'check_multiple',
                    'A resposta é obrigatória',
                    (value: any) => {
                      return !!value;
                    }
                  ),
                };
              }
            }

            return previous;
          }, {} as { [key: string]: Yup.StringSchema<string | undefined, Record<string, any>, string | undefined> }),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const responseCheck = await api.get(
          `tickets/events/${ticketSelected.event_id}/${data.document}`
        );

        if (responseCheck.data.length > 0) {
          const hasDuplicateTicket = responseCheck.data.some(
            (ticketData: any) =>
              ticketData.batch_category_id ===
                ticketSelected.batch_category_id &&
              ticketData.modality_id === ticketSelected.modality_id &&
              ticketData.id !== ticketSelected.ticket_id
          );

          if (hasDuplicateTicket) {
            throw new Error('inscrição repetida');
          }
        }

        let date = '';
        let kit = data.size || '';
        if (data.size) {
          const kitSelected = sizes.find(
            (size) => size.id.toString() === data.size
          );
          if (kitSelected) {
            kit = kitSelected.value;
          }
        }
        if (data.birthdate) {
          const [day, month, year] = data.birthdate.split('/');
          date = `${year}-${month}-${day}`;
        }

        const formData = {
          batch_category_id: ticketSelected.batch_category_id,
          modality_id: ticketSelected.modality_id,
          user_id: ticketSelected.user_id,
          document: data.document,
          name: data.name,
          email: data.email,
          phone: data.phone,
          gender: data.gender,
          birthdate: date,
          kit,
          status: ticketSelected.status === 'Disponível' ? 'Usado' : undefined,
        };

        const response = await api.put(
          `tickets/${ticketSelected.ticket_id}`,
          formData
        );

        if (Object.keys(answersTransfer).length > 0) {
          await Promise.all(
            Object.values(answersTransfer).map((answerArray) => {
              return answerArray.map((answer: IAnswer) =>
                api.post('answers', {
                  ticket_id: response.data.id,
                  event_id: eventId,
                  question_type_id: answer.question_type_id,
                  question_id: answer.question_id,
                  alternative_id: answer.alternative_id,
                  text: answer.text,
                })
              );
            })
          );
        }

        if (answers.length > 0 && Object.keys(answersTransfer).length > 0) {
          const changedQuestionIds = Object.keys(answersTransfer).map((id) =>
            Number(id)
          );
          const answersToDelete = answers.filter(
            (answer) =>
              answer.question_id !== undefined &&
              changedQuestionIds.includes(answer.question_id)
          );
          await Promise.all(
            answersToDelete.map((answer) => api.delete(`answers/${answer.id}`))
          );
        }

        if (ticketSelected.has_kit && kitId) {
          const kitUserFormData = {
            event_id: eventId,
            kit2_id: kitId,
            user_id: ticketSelected.user_id,
            ticket_id: ticketSelected.ticket_id,
            kitQuantity_id: Number.isNaN(parseInt(data.size, 10))
              ? kitQuantityId
              : parseInt(data.size, 10),
          };

          if (kitUserId) {
            await api.put(`kits-users/${kitUserId}`, kitUserFormData);
          } else {
            await api.post('kits-users', kitUserFormData);
          }
        }

        const newOrders = { ...orders };
        const { orderCode } = ticketSelected;

        if (orderCode !== undefined) {
          const ticketIndex = newOrders[orderCode].tickets.findIndex(
            (ticket) => ticket.id === ticketSelected.id
          );

          if (ticketIndex >= 0) {
            newOrders[orderCode].tickets[ticketIndex].name = response.data.name;
            newOrders[orderCode].tickets[
              ticketIndex
            ].document = `****${response.data.document.slice(4, 11)}***`;

            if (ticketSelected.has_kit && kitId) {
              const kitSelected = sizes.find(
                (size) => size.id.toString() === data.size
              );
              newOrders[orderCode].tickets[ticketIndex].kit = kitSelected
                ? kitSelected.value
                : '';
            }
          }
        }

        Swal.fire({
          icon: 'success',
          iconColor: '#ff6900',
          title: `Essa inscrição foi atribuída para ${
            ticketSelected.name.split(' ')[0]
          }`,
          showCloseButton: true,
        }).finally(() => {
          window.location.reload();
        });
        setOrders(newOrders);
        handleClose();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);

          formRef.current?.setErrors(errors);
        } else {
          Swal.fire('Oops...', 'Ocorreu um erro tente novamente, por favor');
        }
      } finally {
        setLoading(false);
      }
    },
    [
      allQuestions,
      ticketSelected,
      answersTransfer,
      answers,
      kitId,
      orders,
      handleClose,
      eventId,
      hasSizeVariation,
      sizes,
      kitQuantityId,
      kitUserId,
    ]
  );

  const questionsWithAnswers = useMemo<IQuestionComponent[]>(
    () =>
      allQuestions.map((question) => {
        const existingAnswers = ticketSelected.questions?.filter(
          (q) => q.question === question.question
        );
        return {
          ...question,
          defaultValue:
            existingAnswers && existingAnswers.length > 0
              ? existingAnswers[0]?.answer
              : undefined,
          alternatives: question.alternatives?.map((alt) => ({
            ...alt,
            selected:
              !!existingAnswers &&
              existingAnswers.some((answer) => answer.answer === alt.text),
          })),
        };
      }),
    [allQuestions, ticketSelected.questions]
  );

  const handleSubmitModCat = useCallback(
    async (data) => {
      Swal.fire({
        icon: 'warning',
        iconColor: '#ff6900',
        title: 'Transferir Modalidade/ Categoria?',
        showCancelButton: true,
        cancelButtonText: 'Não',
        confirmButtonText: 'Sim',
        confirmButtonColor: '#ff6900',
        cancelButtonColor: '#B34A00',
        reverseButtons: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            setLoading(true);
            await api.patch(
              `batches-categories/changes/execute/${ticketSelected.ticket_id}`,
              {
                batch_category_id: data.modCat,
              }
            );
            Toast.fire({
              icon: 'success',
              title: 'Modalidade/ Categoria transferida com sucesso',
            });
          } catch (error) {
            if (error instanceof Yup.ValidationError) {
              const errors = getValidationErros(error);

              formRef.current?.setErrors(errors);
            } else if (axios.isAxiosError(error)) {
              Swal.fire(
                'Atenção',
                error.response?.data?.message ||
                  'Ocorreu um erro tente novamente, por favor',
                'warning'
              );
            } else {
              Swal.fire(
                'Opss...',
                'Ocorreu um erro tente novamente, por favor',
                'error'
              );
            }
          } finally {
            setLoading(false);
            handleCloseModCat();
          }
        }
      });
    },
    [handleCloseModCat, ticketSelected.ticket_id]
  );

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="zoom col-12 px-0">
            <div className="bg-white d-sm-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">Lista de inscrições</h1>
              <ExportCsv
                data={tickets}
                columns={columnsCsv}
                fileName="Lista_de_inscricoes"
                onClick={handleClickExportCsv}
              />
            </div>
          </div>
          <Filter className="col-12 px-0 mt-3">
            <FiltersLists
              onSubmitFilters={handleSubmitFilters}
              name
              codeVoucher
              organizer
              organizersData={organizers}
              event
              order
              ticket
              document
              statusPayment
              paymentsData={paymentStatus}
              // statusTicket
              statusOptions={status}
            />
          </Filter>

          <div className="col-12 px-0">
            <div className="row">
              <TableList className="col-12 mt-3">
                <div className="bg-white pb-2">
                  <div className="bg-white overflow-auto">
                    <div className="">
                      <Table
                        className="mb-0 p-0 table"
                        columns={columns}
                        data={tickets}
                        pagination
                        totalData={tableData.total}
                        selectedPage={pageSelected}
                        onChangePage={handleChangePage}
                        changeQuantity
                        onChangeQuantity={handleChangeQuantity}
                        showTotals
                        generalTotal={tableData.generalTotal}
                      />
                    </div>
                  </div>
                </div>
              </TableList>
            </div>
          </div>
        </div>
      </div>
      <Modal
        className="modal-filter"
        size="lg"
        centered
        show={show}
        onHide={handleClose}
      >
        <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
          <Modal.Title className="mb-0 mt-4">
            <h2 className="title mb-0">Perguntas</h2>
          </Modal.Title>
          <button
            type="button"
            onClick={handleClose}
            className="border-0 bg-transparent"
          >
            <KrClose size={20} color="#989898" />
          </button>
        </Modal.Header>
        <Modal.Body className="pt-0 px-3 px-sm-5">
          <div className="row">
            <div className="col-12">
              <hr />

              <TableListModal className="table-modal">
                <Table columns={columnsModal} data={questions} />
              </TableListModal>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 px-5" />
      </Modal>
      <ModalTransfer
        className="modal-filter"
        size="md"
        centered
        show={showTransfer}
        onHide={handleCloseTransfer}
      >
        <Form
          key={ticketSelected.ticket_id}
          ref={formRef}
          onSubmit={handleSubmit}
        >
          <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pb-lg-4 pt-0">
            <Modal.Title className="mb-3 mt-4">
              <h2 className="title h5 fw-semibold mb-0">Dados da inscrição</h2>
            </Modal.Title>
            <button
              type="button"
              onClick={handleCloseTransfer}
              className="border-0 bg-transparent"
            >
              <IoClose size={20} color="#989898" />
            </button>
          </Modal.Header>
          <Modal.Body className="pt-0 px-3 px-sm-5 mx-lg-4">
            <div className="mb-3">
              <h5 className="h6">
                <b>Modalidade:</b> {ticketSelected.modality}
              </h5>
              <h5 className="h6">
                <b>Categoria:</b> {ticketSelected.category}
              </h5>
            </div>

            <label className="mb-3">
              <span className="fw-normal d-block mb-2">Documento*</span>
              <div className="d-flex">
                <Select
                  className="bd-input w-25"
                  name="documents"
                  readOnly
                  options={[
                    {
                      value: 'CPF',
                      id: 'CPF',
                      selected: true,
                    },
                  ]}
                />
                <div className="mt-0 ms-1 w-75">
                  <InputMask
                    kind="cpf"
                    name="document"
                    className="bd-input"
                    value={ticketSelected.document}
                    // error={ticketError}
                    onChange={handleChangeDocument}
                  />
                </div>
              </div>
            </label>

            <label className="mb-3 w-100">
              <span className="fw-normal d-block mb-2">Nome Completo*</span>
              <Input
                name="name"
                className="bd-input"
                defaultValue={ticketSelected.name}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(
                    /[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g,
                    ''
                  );
                }}
              />
            </label>

            <label className="mb-3 w-100">
              <span className="fw-normal d-block mb-2">Data de nascimento</span>
              <InputMask
                kind="custom"
                options={{ mask: '99/99/9999' }}
                name="birthdate"
                className="bd-input"
                value={ticketSelected.birthdate}
                inputMode="numeric"
              />
            </label>

            <label className="mb-3 w-100">
              <span className="fw-normal d-block mb-2">Gênero</span>
              <InputRadio
                name="gender"
                className="d-flex justify-content-start check"
                options={[
                  {
                    id: 'M',
                    value: 'Masculino',
                  },
                  {
                    id: 'F',
                    value: 'Feminino',
                  },
                ]}
                selected={genderSelected}
                onChange={(e) => setGenderSelected(e as IOption)}
              />
            </label>

            <label className="mb-3 w-100">
              <span className="fw-normal d-block mb-2">Telefone*</span>
              <InputMask
                kind="cel-phone"
                name="phone"
                className="bd-input"
                value={ticketSelected.phone}
              />
            </label>

            <label className="mb-3 w-100">
              <span className="fw-normal d-block mb-2 size">E-mail*</span>
              <Input
                type="email"
                name="email"
                className="bd-input"
                defaultValue={ticketSelected.email}
              />
            </label>

            {ticketSelected.has_kit && sizesWithSelection.length > 0 && (
              <label className="mb-3 w-100">
                <span className="fw-normal d-block mb-2">Tamanho do kit*</span>
                <Select
                  className="bd-input select-tam"
                  name="size"
                  options={sizesWithSelection}
                />
              </label>
            )}
            {questionsWithAnswers.length > 0 && (
              <div className="bg-information">
                <h2 className="h6">Mais informações</h2>
                {questionsWithAnswers.map((question) => (
                  <Question
                    key={question.id}
                    question={question}
                    onChangeAnswer={(answer) => {
                      setAnswersTransfer((prev) => ({
                        ...prev,
                        [question.id]: answer,
                      }));
                    }}
                  />
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-5">
            <button
              type="submit"
              // disabled={sending}
              className="w-100 mx-0 btn-filter d-flex align-items-center justify-content-center btn h6 fw-normal py-2 mt-2 mb-0 mx-4 btn-login"
            >
              Transferir inscrição
            </button>
          </Modal.Footer>
        </Form>
      </ModalTransfer>
      <ModalTransferModCat
        className="modal-filter"
        key={ticketSelected.ticket_id}
        size="lg"
        centered
        show={showModCat}
        onHide={handleCloseModCat}
      >
        <Form onSubmit={handleSubmitModCat}>
          <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
            <Modal.Title className="mb-0 mt-4">
              <h2 className="title mb-3">Transferir modalidade e categoria</h2>
            </Modal.Title>
            <button
              type="button"
              onClick={handleCloseModCat}
              className="border-0 bg-transparent"
            >
              <KrClose size={20} color="#989898" />
            </button>
          </Modal.Header>
          <Modal.Body className="pt-0 px-3 px-sm-5">
            <div className="row">
              <div className="col-12">
                <div className="border p-3 mb-4">
                  <h6>
                    Nome:{' '}
                    <span className="fw-semibold">{modCat?.current.name}</span>
                  </h6>
                  <h6>
                    Inscrição:{' '}
                    <span className="fw-semibold">{ticketSelected.id}</span>
                  </h6>
                  <h6 className="d-flex justify-content-between">
                    <span>
                      Data:{' '}
                      <span className="fw-semibold">
                        {modCat?.current &&
                          formatDate(
                            modCat.current.purchase_date,
                            'dd/MM/yyyy'
                          )}
                      </span>
                    </span>
                    <span>
                      <span className="fw-semibold">
                        {modCat?.current.batch}
                      </span>
                    </span>
                  </h6>
                  <h6 className="d-flex">
                    <span className="d-block fw-semibold">
                      {modCat?.current.modality} - {modCat?.current.category}
                    </span>
                    <div className="pontos" />
                    <span className="d-block text-end fw-semibold">
                      {modCat?.current && formatPrice(modCat?.current.price)}
                    </span>
                  </h6>
                </div>

                <div className="p-2">
                  <h5 className="title-mod text-center mb-3">
                    Modalidades e categorias disponíveis
                  </h5>
                  {modCat?.current && (
                    <InputCheckbox
                      type="radio"
                      name="modCat"
                      className="check-mod-cat mt-2 flex-column justify-content-between"
                      labelHtml
                      options={modCat.availables.map((available) => ({
                        label: `<div class="d-flex alinhar-preenchimento"><span class="texto-fixo">${
                          available.modality
                        } - ${
                          available.category
                        }</span><div class="pontos"></div><span class="texto-fixo">${formatPrice(
                          available.price
                        )}</span></div>`,
                        value: available.batch_category_id,
                        selected:
                          selectedModCat === available.batch_category_id,
                      }))}
                      onChange={(options) => {
                        const selected = options.find(
                          (option) => option.selected
                        );

                        setSelectedModCat(
                          selected ? Number(selected.value) : null
                        );
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-5">
            <button
              type="button"
              onClick={handleCloseModCat}
              className="btn mx-0 d-flex align-items-center justify-content-center btn h6 fw-normal py-2 mt-2 mb-0 mx-4 btn-cancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={selectedModCat === null}
              className="btn btn-filter mx-0 d-flex align-items-center justify-content-center btn h6 fw-normal py-2 mt-2 mb-0 mx-4 btn-login"
            >
              Transferir
            </button>
          </Modal.Footer>
        </Form>
      </ModalTransferModCat>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Users;
